import {
    Box,
    Button,
    createTheme,
    Grid,
    IconButton,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    ThemeProvider,
    Typography
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import useStyles from "../maintenance/assets/styles";
import { SearchBar } from "src/components/sub-components/SearchBar";
import { Arrow, DeviceFailureIcon, DownArrowIcon, EditConfigurationIcon, FilterIcon, JiraIcon, NonSuccessCriteriaIcon, RefreshApiIcon, ServiceNowIcon, TrashIcon, UpArrow } from "src/components/Icons";
import { Link } from "react-router-dom";
import { FilterBatchNumber } from "../maintenance/test-automation/components/FilterBatchNumber";
import { EditConfigurationPopUp } from "./components/EditConfiguration";
import { getAllDBJiraIssues, getAllDBServiceNowIssues, getAllJiraIssues, getAllJiraStatuses, getAllServiceNowIssues, getJiraProject, getServiceNowProject, SyncJiraTickets, SyncServiceNowTickets } from "../maintenance/test-automation/components/apiHelper";
import { Loader } from "../maintenance/test-automation/components/Loader";
import { ToasterComponent } from "../maintenance/test-automation/components/Toaster";
import { useLocation } from "react-router-dom";
import { devicePulseStatus, formatSriLankanDateTime, getBackgroundColor, getColor, getStatusBackgroundColor, getStatusColor } from "../utils";
import { ProjectDetail, ITSMTypeEnum } from "../models";
import { DropDown } from "../maintenance/test-automation/components/DropDown";
import { hasUserPermission } from "../permission";

export const DeviceFailureComponent = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterVisible, setFilterVisible] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [isEditPopUpOpen, setIsEditPopUpOpen] = useState(false);
    const [projectDetail, setProjectDetail] = useState<ProjectDetail>({});
    const [isJiraProjectExist, setIsJiraProjectExist] = useState(false);
    const [isServiceNowProjectExist, setIsServiceNowProjectExist] = useState(false);
    const location = useLocation();

    const [combinedIssues, setCombinedIssues] = useState([]);
    const [filteredIssues, setFilteredIssues] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [jiraStatuses, setJiraStatuses] = useState([]);
    const [statusMapping, setStatusMapping] = useState({});
    const [mappingErrors, setMappingErrors] = useState<any>({});
    const [filters, setFilters] = useState({
        status: null,
        deviceId: null
    });

    const classes = useStyles();

    const dropDownOptions = [
        {
            key: ITSMTypeEnum.JIRA,
            display: "JIRA",
            component: (
                <Grid item key={ITSMTypeEnum.JIRA} className={classes.itsmRowContainer}>
                    <IconButton>
                        <JiraIcon />
                    </IconButton>
                    <Grid item className={classes.itsmColumnContainer}>
                        <Typography className={classes.dropDownTitle}>JIRA</Typography>
                        <Typography className={classes.dropDownSubTitle}>Atlassian JIRA Integration</Typography>
                    </Grid>
                </Grid>
            )
        },
        {
            key: ITSMTypeEnum.SERVICENOW,
            display: "ServiceNow",
            component: (
                <Grid item key={ITSMTypeEnum.SERVICENOW} className={classes.itsmRowContainer}>
                    <IconButton>
                        <ServiceNowIcon />
                    </IconButton>
                    <Grid item className={classes.itsmColumnContainer}>
                        <Typography className={classes.dropDownTitle}>ServiceNow</Typography>
                        <Typography className={classes.dropDownSubTitle}>ServiceNow Platform</Typography>
                    </Grid>
                </Grid>
            )
        }
    ];
    const [selectedITSM, setSelectedITSM] = useState<ITSMTypeEnum | null>(isJiraProjectExist ? ITSMTypeEnum.JIRA : isServiceNowProjectExist ? ITSMTypeEnum.SERVICENOW : null);    const [isOptionOpen, setIsOptionOpen] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [uniqueDeviceIds, setUniqueDeviceIds] = useState([]);

    //Toaster
    const [isToasterOpen, setIsToasterOpen] = useState(false)
    const [toasterMessage, setToasterMessage] = useState("")
    const [reqSuccess, setReqSuccess] = useState(false)
    const [loading, setLoading] = useState(false)

    const theme = createTheme({
        typography: {
            fontFamily: "'Poppins', sans-serif",
        },
    });

    const toggleDropdown = () => setIsOptionOpen(!isOptionOpen);

    const handleSelect = async (key) => {
        setSelectedITSM(key);
        setIsOptionOpen(false);
        
        try {
            setLoading(true);
            let response;
            if (key === ITSMTypeEnum.JIRA) {
                response = await getJiraProject();
            } else if (key === ITSMTypeEnum.SERVICENOW) {
                response = await getServiceNowProject();
            }

            if (response && response.status === 200) {
                setProjectDetail(response.data);
                
                const initialMapping = {};
                devicePulseStatus.forEach((dpStatus, index) => {
                    initialMapping[index] = response.data.statusMap[dpStatus.status] || "";
                });
                setStatusMapping(initialMapping);
                
                const newStatusOptions = await fetchStatusOptions();
                setStatusOptions(newStatusOptions);
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedITSM === ITSMTypeEnum.JIRA) {
            fetchJiraData();
        } else if (selectedITSM === ITSMTypeEnum.SERVICENOW) {
            fetchServiceNowData();
        }
    }, [selectedITSM]);

    const handleSearch = (event) => {
        const { value } = event.target
        const lowerCaseSearchTerm = value.toLowerCase();
        if (!lowerCaseSearchTerm) {
            setFilteredIssues(combinedIssues);
            return;
        }
        let filterIssue = combinedIssues.filter(issue => {
            const matchesPrefix = selectedStatus === 'ALL' || issue.status.includes(selectedStatus);
            const matchesSearch = issue.deviceId.toLowerCase().includes(lowerCaseSearchTerm) || issue.jiraTicketNumber.toLowerCase().includes(lowerCaseSearchTerm);
            return matchesSearch && matchesPrefix;
        });
        setFilteredIssues(filterIssue);
        setPage(0);
    }

    const applyFilter = () => {
        if (!filters.status && !filters.deviceId) {
            setFilteredIssues(combinedIssues);
        } else {
            const filtered = combinedIssues.filter(issue => {
                // Device ID filter
                let deviceMatch = true;
                if (filters.deviceId) {
                    deviceMatch = issue.deviceId === filters.deviceId;
                }

                // Status filter
                let statusMatch = true;
                if (filters.status) {
                    const selectedOption = statusOptions.find(
                        opt => opt.jiraStatus === filters.status
                    );

                    if (selectedOption) {
                        statusMatch = issue.status === selectedOption.jiraStatus;

                        if (projectDetail.statusMap) {
                            const mappedStatus = Object.entries(projectDetail.statusMap)
                                .find(([dpStatus, js]) => js === issue.status);

                            if (mappedStatus) {
                                const [dpStatus] = mappedStatus;
                                statusMatch = statusMatch || dpStatus === selectedOption.status;
                            }
                        }
                    }
                }

                return deviceMatch && statusMatch;
            });

            setFilteredIssues(filtered);
        }

        setFilterVisible(false);
        setPage(0);
    };

    const cancelFilter = () => {
        setFilters({
            status: null,
            deviceId: null
        });
        setFilterVisible(false);
    };

    const filterStatus = () => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const filtered = combinedIssues.filter(issue => {
            const matchesPrefix = selectedStatus === 'All' || issue.status.includes(selectedStatus);
            const matchesSearch = issue.jiraTicketNumber.toLowerCase().includes(lowerCaseSearchTerm) || issue.deviceId.toLowerCase().includes(lowerCaseSearchTerm);
            return matchesPrefix && matchesSearch;
        });
        setFilteredIssues(filtered);
        setFilterVisible(false);
        setPage(0)
    }

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const [initialStatusOptions] = await Promise.all([
                    fetchStatusOptions(),
                    fetchProjects()
                ]);
                setStatusOptions(initialStatusOptions);
            } catch (error) {
                console.error("Initial load error:", error);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

   const fetchStatusOptions = async () => {
        try {
            if (!selectedITSM) return [];
            
            const projectResponse = selectedITSM === ITSMTypeEnum.JIRA 
                ? await getJiraProject() 
                : await getServiceNowProject();
                
            const statusesResponse = selectedITSM === ITSMTypeEnum.JIRA 
                ? await getAllJiraStatuses() 
                : await getAllServiceNowIssues();

            if (projectResponse.status === 200) {
                const projectData = projectResponse.data;
                const allStatuses = selectedITSM === ITSMTypeEnum.JIRA ? 
                    statusesResponse.data.flatMap(issueType => issueType.statuses.map(status => status.name)) :
                    statusesResponse.data.result.map(status => status); 

                const uniqueStatuses = Array.from(new Set(allStatuses));

                const mappedOptions = devicePulseStatus.map(dpStatus => {
                    let itsmStatus = 'N/A';
                    if (projectData.statusMap && projectData.statusMap[dpStatus.status]) {
                        itsmStatus = projectData.statusMap[dpStatus.status];
                    }
                    return {
                        ...dpStatus,
                        jiraStatus: itsmStatus,
                        displayName: `${dpStatus.status} (${itsmStatus})`
                    };
                });

                const unmappedStatuses = uniqueStatuses
                    .filter(js => {
                        if (!projectData.statusMap) return true;
                        return !Object.values(projectData.statusMap).includes(js);
                    })
                    .map(js => ({
                        status: 'N/A',
                        jiraStatus: js,
                        displayName: `N/A (${js})`,
                        id: `unmapped-${js}`
                    }));

                return [...mappedOptions, ...unmappedStatuses];
            }
            return [];
        } catch (error) {
            console.error("Error fetching status options:", error);
            return [];
        }
    };

    const handleEditPopUp = () => {
        setIsEditPopUpOpen(true)
    }

    const cancelEditPopup = () => {
        setIsEditPopUpOpen(false)

        if (projectDetail.statusMap) {
            const initialMapping = {};
            devicePulseStatus.forEach((dpStatus, index) => {
                initialMapping[index] = projectDetail.statusMap[dpStatus.status] || "";
            });
            setStatusMapping(initialMapping);
        }
    }

    const toggleFilter = () => {
        setFilterVisible(!filterVisible);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const jiraPromise = getJiraProject().catch(error => error);
            const serviceNowPromise = getServiceNowProject().catch(error => error);
            const [jiraResult, serviceNowResult] = await Promise.all([jiraPromise, serviceNowPromise]);

            // Process Jira response
            let jiraData = null;
            const isJiraSuccessful = jiraResult.status === 200
                && jiraResult.data
                && !jiraResult.data.errorCode;

            if (isJiraSuccessful) {
                jiraData = jiraResult.data;
                setIsJiraProjectExist(true);
                setProjectDetail(jiraData);

                const initialMapping = {};
                devicePulseStatus.forEach((dpStatus, index) => {
                    initialMapping[index] = jiraData.statusMap[dpStatus.status] || "";
                });
                setStatusMapping(initialMapping);

                const mappedOptions = devicePulseStatus.map(dpStatus => {
                    const jiraStatus = jiraData.statusMap[dpStatus.status] || '';
                    return {
                        ...dpStatus,
                        jiraStatus: jiraStatus,
                        displayName: `${dpStatus.status} (${jiraStatus})`
                    };
                });

                setStatusOptions(mappedOptions);

                setSelectedITSM(ITSMTypeEnum.JIRA);
            } else {
                const errorMessage = jiraResult.data ? jiraResult.data.message : 'Unknown Jira error';
                console.log("Jira project not found or error:", errorMessage);
            }

            // Process ServiceNow response
            const isServiceNowSuccessful = serviceNowResult.status === 200
                && serviceNowResult.data
                && !serviceNowResult.data.errorCode;

            if (isServiceNowSuccessful) {
                const serviceNowData = serviceNowResult.data;
                setIsServiceNowProjectExist(true);

                const shouldUseServiceNow = !jiraData || !jiraData.jiraBoard;
                if (shouldUseServiceNow) {
                    setProjectDetail(serviceNowData);

                    const initialMapping = {};
                    devicePulseStatus.forEach((dpStatus, index) => {
                        initialMapping[index] = serviceNowData.statusMap[dpStatus.status] || "";
                    });
                    setStatusMapping(initialMapping);

                    const mappedOptions = devicePulseStatus.map(dpStatus => {
                        const serviceNowStatus = serviceNowData.statusMap[dpStatus.status] || '';
                        return {
                            ...dpStatus,
                            jiraStatus: serviceNowStatus,
                            displayName: `${dpStatus.status} (${serviceNowStatus})`
                        };
                    });

                    setStatusOptions(mappedOptions);

                    setSelectedITSM(ITSMTypeEnum.SERVICENOW);
                }
            } else {
                const errorMessage = serviceNowResult.data ? serviceNowResult.data.message : 'Unknown ServiceNow error';
                console.log("ServiceNow project not found or error:", errorMessage);
            }

            if (isJiraSuccessful && !selectedITSM) {
                setProjectDetail(jiraData);
                const initialMapping = {};
                devicePulseStatus.forEach((dpStatus, index) => {
                    initialMapping[index] = jiraData.statusMap[dpStatus.status] || "";
                });
                setStatusMapping(initialMapping);
                setSelectedITSM(ITSMTypeEnum.JIRA);
            }
        } catch (error) {
            console.log("Unexpected error in fetchProjects:", error);
        } finally{
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isJiraProjectExist) {
            fetchAllJiraStatuses();
        }
    }, [isJiraProjectExist]);

    const fetchAllJiraStatuses = async () => {
        try {
            setLoading(true);
            const response = await getAllJiraStatuses();
            if (response.status === 200) {
                const data = response.data;

                const allStatusNames = data.flatMap((issueType: any) =>
                    issueType.statuses.map((status: any) => status.name)
                );

                const uniqueStatusNames = [...new Set(allStatusNames)];

                setJiraStatuses(uniqueStatusNames);
            } else {
                console.log("Error fetching data");
            }
        }
        catch (error) {
            setIsToasterOpen(true);
            setReqSuccess(false);
            setToasterMessage(error.message || 'Something Went Wrong');
        }
        finally {
            setLoading(false);
            setTimeout(() => setIsToasterOpen(false), 2000);
        }
    }

    const handleJiraStatusChange = (index: number, selectedStatus: string) => {
        setStatusMapping((prev) => {
            const updatedMapping = {
                ...prev,
                [index]: selectedStatus,
            };

            setMappingErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (selectedStatus && selectedStatus.trim() !== "") {
                    newErrors[index] = null;
                }
                return newErrors;
            });

            return updatedMapping;
        });
    };

    const fetchServiceNowData = async () => {
        try {
            setLoading(true);
            setCombinedIssues([]);
            setFilteredIssues([]);
            setUniqueDeviceIds([]);

            const [dbResponse, serviceNowResponse] = await Promise.all([
                getAllDBServiceNowIssues(),
                getAllServiceNowIssues()
            ]);

            if (dbResponse.status !== 200 || serviceNowResponse.status !== 200) {
                setCombinedIssues([]);
                setFilteredIssues([]);
                setUniqueDeviceIds([]);
                return;
            }

            const dbIssues = dbResponse.data.reverse();
            const serviceNowIssues = serviceNowResponse.data.result || [];

            const serviceNowIssuesMap = new Map();
            serviceNowIssues.forEach(issue => {
                serviceNowIssuesMap.set(issue.ticketid, issue);
            });

            const combined = dbIssues.map(dbIssue => {
                const serviceNowIssue = serviceNowIssuesMap.get(dbIssue.ticketId);
                
                return {
                    jiraTicketNumber: dbIssue.ticketId || 'N/A',
                    subTestcase: dbIssue.subTestcase || 'N/A',
                    status: serviceNowIssue ? serviceNowIssue.status : 'N/A',
                    reporter: serviceNowIssue ? serviceNowIssue.reporter : 'N/A',
                    assignee: serviceNowIssue ? serviceNowIssue.assignedto : 'N/A',
                    created: serviceNowIssue && serviceNowIssue.sys_created_on
                        ? formatSriLankanDateTime(serviceNowIssue.sys_created_on)
                        : (dbIssue.createdDate 
                            ? formatSriLankanDateTime(dbIssue.createdDate) 
                            : 'N/A'),
                    deviceId: dbIssue.deviceId || 'N/A',
                    lastUpdated: dbIssue.lastUpdatedDate || 'N/A',
                };
            });

            const deviceIds = [...new Set(combined.map(issue => issue.deviceId))].filter(id => id !== 'N/A');
            setUniqueDeviceIds(deviceIds);
            setCombinedIssues(combined);
            setFilteredIssues(combined);
        } catch (error) {
            console.log("Error fetching ServiceNow data:", error);
            setCombinedIssues([]);
            setFilteredIssues([]);
            setUniqueDeviceIds([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchJiraData = async () => {
        try {
            setLoading(true);
            setCombinedIssues([]);
            setFilteredIssues([]);
            setUniqueDeviceIds([]);

            const [dbResponse, jiraResponse] = await Promise.all([
                getAllDBJiraIssues(),
                getAllJiraIssues()
            ]);

            if (dbResponse.status !== 200 || jiraResponse.status !== 200) {
                setCombinedIssues([]);
                setFilteredIssues([]);
                setUniqueDeviceIds([]);
                return;
            }

            const dbIssues = dbResponse.data.filter(issue => issue.type === "SUB");
            const jiraSubTasks = jiraResponse.data.issues.filter(
                issue => issue.fields.issuetype.subtask === true
            );

            const jiraIssuesMap = new Map();
            jiraSubTasks.forEach(issue => {
                jiraIssuesMap.set(issue.key, issue);
            });

            const combined = dbIssues.map(dbIssue => {
                const jiraIssue = jiraIssuesMap.get(dbIssue.issueKey);
                
                return {
                    jiraTicketNumber: dbIssue.issueKey || 'N/A',
                    subTestcase: dbIssue.subTestcase || 'N/A',
                    status: jiraIssue ? jiraIssue.fields.status.name : 'N/A',
                    reporter: jiraIssue ? jiraIssue.fields.reporter.displayName : 'N/A',
                    assignee: jiraIssue && jiraIssue.fields.assignee 
                        ? jiraIssue.fields.assignee.displayName 
                        : 'N/A',
                    created: jiraIssue 
                        ? formatSriLankanDateTime(jiraIssue.fields.created) 
                        : (dbIssue.createdDate 
                            ? formatSriLankanDateTime(dbIssue.createdDate) 
                            : 'N/A'),
                    deviceId: dbIssue.deviceId || 'N/A',
                    lastUpdated: dbIssue.lastUpdatedDate || 'N/A',
                };
            });

            const deviceIds = [...new Set(combined.map(issue => issue.deviceId))].filter(id => id !== 'N/A');
            setUniqueDeviceIds(deviceIds);
            setCombinedIssues(combined);
            setFilteredIssues(combined);
        } catch (error) {
            console.log("Error fetching Jira data:", error);
            setCombinedIssues([]);
            setFilteredIssues([]);
            setUniqueDeviceIds([]);
        } finally {
            setLoading(false);
        }
    };

    const syncTickets = async () => {
        try {
            setLoading(true);
            let response;
            if (selectedITSM === ITSMTypeEnum.JIRA) {
                response = await SyncJiraTickets();
            } else if (selectedITSM === ITSMTypeEnum.SERVICENOW) {
                response = await SyncServiceNowTickets();
            }

            if (response && response.status === 200) {
                const newProjectDetail = selectedITSM === ITSMTypeEnum.JIRA
                    ? await getJiraProject()
                    : await getServiceNowProject();

                const newStatusOptions = await fetchStatusOptions();

                if (selectedITSM === ITSMTypeEnum.JIRA) {
                    await fetchJiraData();
                } else {
                    await fetchServiceNowData();
                }

                setStatusOptions(newStatusOptions);
                setProjectDetail(newProjectDetail.data);

                setFilters({
                    status: null,
                    deviceId: null
                });

                setIsToasterOpen(true);
                setReqSuccess(true);
                setToasterMessage(response.data.status);
            }
        } catch (error) {
            setIsToasterOpen(true);
            setReqSuccess(false);
            if (error.response && error.response.data && error.response.data.status) {
                setToasterMessage(error.response.data.status);
            } else {
                setToasterMessage("Sync failed");
            }
        } finally {
            setLoading(false);
            const timer = setTimeout(() => {
                setIsToasterOpen(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    };

    return (
        <ThemeProvider theme={theme} >
            <Grid container style={{ padding: '25px' }}>
                <Grid item xs={12} md={12}>
                    <Grid className={classes.pageTitle} style={{ margin: '0px' }}>Failure Analytics</Grid>
                    <Grid container style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Grid container className={classes.redirectContainer}>
                            <Grid item style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                <Link to={'/TestAutomation'} style={{ paddingLeft: '10px', textDecoration: 'none' }}>
                                    <NonSuccessCriteriaIcon />
                                </Link>
                            </Grid>
                            <Arrow />
                            <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                <DeviceFailureIcon />
                                <Typography className={classes.redirectTitle}>Failure Analytics</Typography>
                            </Grid>
                        </Grid>
                        {(isJiraProjectExist || isServiceNowProjectExist) && (
                            <>
                                <Grid item xs={7} md={7} className={classes.configureContainer}>
                                    <Grid item xs={4} md={4} style={{ position: 'relative' }}>
                                        <Grid
                                            onClick={toggleDropdown}
                                            className={classes.dropDownContainer}
                                        >
                                            {selectedITSM
                                                ? dropDownOptions.find(opt => opt.key === selectedITSM).component
                                                : <span style={{ color: '#999' }}>Select ITSM</span>
                                            }
                                            <Grid>
                                                {isOptionOpen ? <UpArrow /> : <DownArrowIcon />}
                                            </Grid>
                                        </Grid>

                                        {isOptionOpen && (
                                            <Grid className={classes.configureInput}>
                                                {dropDownOptions.filter(option =>
                                                    (option.key === 'jira' && isJiraProjectExist) ||
                                                    (option.key === 'service-now' && isServiceNowProjectExist)
                                                ).map(option => (
                                                    <Grid
                                                        key={option.key}
                                                        onClick={() => handleSelect(option.key)}
                                                        className={classes.configureOption}
                                                    >
                                                        {option.component}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        )}
                                    </Grid>
                                    { hasUserPermission("FAILURE_ANALYTICS", "EDIT") && (
                                        <Button
                                            variant="contained"
                                            className={`${classes.getStarted} ${classes.button}`}
                                            style={{ height: '45px', width: '200px', backgroundColor: '#005B96' }}
                                            startIcon={<EditConfigurationIcon />}
                                            onClick={handleEditPopUp}
                                        >
                                            Edit Configuration
                                        </Button>
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Grid container className={classes.backgroundContainer}>
                        <Grid item xs={12} md={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center' }}>
                                <Grid item xs={6} md={3} style={{ marginRight: '10px' }}>
                                    <SearchBar onChange={handleSearch} />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <Grid item className="filter-validity" style={{ position: 'relative', display: 'inline-block', zIndex: 1 }}>
                                        <div className="filerButton p-relative" onClick={toggleFilter} style={{ display: 'flex', alignItems: 'center' }}>
                                            <FilterIcon />
                                            <span style={{ marginLeft: '4px' }}>Filter</span>
                                        </div>
                                        {filterVisible && (
                                            <div className={classes.filterContainer} style={{ width: "280px" }}>
                                                <div className={classes.filterHeadingContainer}>
                                                    Filter
                                                </div>
                                                <div className={classes.subFilterContainer}>
                                                    <div className={classes.filterRadioContainer}>
                                                        <Radio
                                                            value="ALL"
                                                            checked={!filters.status && !filters.deviceId}
                                                            className={classes.radio}
                                                            onChange={() => setFilters({
                                                                status: null,
                                                                deviceId: null
                                                            })}
                                                            color="default"
                                                        />
                                                        <label style={{ marginLeft: '8px' }}>ALL</label>
                                                    </div>

                                                    <div className={classes.filterSubHeading}>
                                                        Status
                                                    </div>
                                                    {statusOptions.map((statusOption) => (
                                                        <div key={statusOption.id} className={classes.filterRadioContainer}>
                                                            <Radio
                                                                className={classes.radio}
                                                                value={statusOption.status}
                                                                checked={filters.status === statusOption.status}
                                                                onChange={() => setFilters(prev => ({
                                                                    ...prev,
                                                                    status: statusOption.status
                                                                }))}
                                                                color="default"
                                                            />
                                                            <label style={{ marginLeft: '8px' }}>
                                                                {statusOption.displayName || `${statusOption.status} (${statusOption.jiraStatus})`}
                                                            </label>
                                                        </div>
                                                    ))}

                                                    <div className={classes.filterSubHeading}>
                                                        Device Id
                                                    </div>
                                                    {uniqueDeviceIds.map((deviceId, index) => (
                                                        <div key={index} className={classes.filterRadioContainer}>
                                                            <Radio
                                                                className={classes.radio}
                                                                value={deviceId}
                                                                checked={filters.deviceId === deviceId}
                                                                onChange={() => setFilters(prev => ({
                                                                    ...prev,
                                                                    deviceId: deviceId
                                                                }))}
                                                                color="default"
                                                            />
                                                            <label style={{ marginLeft: '8px' }}>{deviceId}</label>
                                                        </div>
                                                    ))}

                                                    <div className={classes.filterButtonContainer}>
                                                        <Button onClick={cancelFilter} className={classes.cancelButton} style={{ marginBottom: '0px', width: '45%' }}>Cancel</Button>
                                                        <Button onClick={applyFilter} className={classes.addButton} style={{ backgroundColor: '#005B96', color: 'white' }}>
                                                            Apply Filter
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item className={classes.buttonContainer} style={{ marginBottom: "10px", justifyContent: 'flex-end' }}>
                                <Button onClick={syncTickets} className={classes.refreshButton}>
                                    <RefreshApiIcon />
                                    <Typography style={{ marginLeft: "10px" }}>
                                        Sync {selectedITSM === ITSMTypeEnum.JIRA ? 'Jira' : 'ServiceNow'}
                                    </Typography>
                                </Button>
                            </Grid>
                        </Grid>
                        <TablePagination style={{ marginRight: '2px' }}
                            rowsPerPageOptions={[]}
                            component="div"
                            count={filteredIssues !== undefined && filteredIssues.filter((issue) => issue.deviceId.toLowerCase().includes(searchTerm.trim().toLowerCase())).length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" className={classes.tableHeader}>Device ID</TableCell>
                                    <TableCell align="left" className={classes.tableHeader}>Failure Timestamp</TableCell>
                                    <TableCell align="left" className={classes.tableHeader}>Failure</TableCell>
                                    <TableCell align="left" className={classes.tableHeader}>{selectedITSM === ITSMTypeEnum.JIRA ? 'Jira' : 'ServiceNow'} Ticket Number</TableCell>
                                    <TableCell align="left" className={classes.tableHeader}>{selectedITSM === ITSMTypeEnum.JIRA ? 'Jira' : 'ServiceNow'} Status</TableCell>
                                    <TableCell align="center" className={classes.tableHeader}>Reporter</TableCell>
                                    <TableCell align="center" className={classes.tableHeader}>Assignee</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredIssues.length > 0 ?
                                    (filteredIssues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((issue, index) => (
                                        <TableRow key={index} style={{ backgroundColor: index % 2 == 0 ? '#F5F5F5' : '#FFFFFF' }}>
                                            <TableCell >{issue.deviceId}</TableCell>
                                            <TableCell>{issue.created !== 'N/A' ? issue.created : 'N/A'}</TableCell>
                                            <TableCell>{issue.subTestcase ? issue.subTestcase : 'N/A'}</TableCell>
                                            <TableCell>
                                                <Typography align="left" style={{ fontSize: "14px" }}>
                                                    {issue.jiraTicketNumber && issue.jiraTicketNumber != '' ? issue.jiraTicketNumber : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Grid item className={classes.jiraStatusBox}>
                                                    {(() => {
                                                        let mappedDpStatus;
                                                        if (projectDetail && projectDetail.statusMap) {
                                                            for (const [dpStatus, js] of Object.entries(projectDetail.statusMap)) {
                                                                if (js === issue.status) {
                                                                    mappedDpStatus = dpStatus;
                                                                    break;
                                                                }
                                                            }
                                                        }

                                                        let displayText;
                                                        if (mappedDpStatus) {
                                                            displayText = `${mappedDpStatus} (${issue.status})`;
                                                        } else {
                                                            displayText = `N/A (${issue.status})`;
                                                        }

                                                        let statusColor = '#5E6C84';
                                                        let bgColor = '#EBECF0';
                                                        if (issue.status) {
                                                            statusColor = getStatusColor(issue.status, projectDetail.statusMap);
                                                            bgColor = getStatusBackgroundColor(issue.status, projectDetail.statusMap);
                                                        }

                                                        return (
                                                            <>
                                                                <Box
                                                                    display="flex"
                                                                    className={classes.jiraStatus}
                                                                    style={{
                                                                        color: statusColor,
                                                                        backgroundColor: bgColor,
                                                                        width: "fit-content"
                                                                    }}
                                                                >
                                                                    {displayText}
                                                                </Box>
                                                                <Typography className={classes.jiraTypo}>
                                                                    Last Update: {issue.lastUpdated}
                                                                </Typography>
                                                            </>
                                                        );
                                                    })()}
                                                </Grid>
                                            </TableCell>
                                            <TableCell>
                                                <Typography align="center" style={{ fontSize: "14px" }}>
                                                    {issue.reporter}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography align="center" style={{ fontSize: "14px" }}>
                                                    {issue.assignee}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )))
                                    : (
                                        <TableRow>
                                            <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                                                No Issues Available
                                            </TableCell>
                                        </TableRow>
                                    )
                                }
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>
            </Grid>
            <EditConfigurationPopUp
                open={isEditPopUpOpen}
                handleWindowClose={cancelEditPopup}
                setIsToasterOpen={setIsToasterOpen}
                setReqSuccess={setReqSuccess}
                setToasterMessage={setToasterMessage}
                setLoading={setLoading}
                projectDetail={projectDetail}
                fetchData={selectedITSM === ITSMTypeEnum.JIRA ? fetchJiraData : fetchServiceNowData}
                jiraStatuses={jiraStatuses}
                handleJiraStatusChange={handleJiraStatusChange}
                statusMapping={statusMapping}
                setStatusMapping={setStatusMapping}
                mappingErrors={mappingErrors}
                setMappingErrors={setMappingErrors}
            />
            <Loader loading={loading} />
            <ToasterComponent
                reqSuccess={reqSuccess}
                message={toasterMessage}
                toaster={isToasterOpen}
            />
        </ThemeProvider>
    )
};